"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 90: J.Y. Company ASIC
 * ------------------------------------------------------------------ *
 * The 晶太 (Jing Tai / J.Y. Company) ASIC is the board behind a large batch of
 * late pirate and Hummer Team cartridges (Aladdin, whose alternative titles
 * include Popeye II: Travels in Persia, Mortal Kombat II Special, Contra
 * Spirits...). It is similar in spirit to the MMC5: several switchable PRG and
 * CHR window layouts, single-screen mirroring, and a flexible 8-bit IRQ counter.
 *
 * Register behaviour follows https://www.nesdev.org/wiki/J.Y._Company_ASIC :
 *   $8000-$8003  PRG bank select (one name per window; only $8001/$8003 are
 *                used in 16 KB mode and only $8003 in 32 KB mode)
 *   $9000-$9007  CHR bank select, low byte
 *   $A000-$A007  CHR bank select, high byte (bank number = high << 8 | low)
 *   $B000-$B007  ROM nametable / CIRAM bank select
 *   $C000-$C007  IRQ counter
 *   $D000-$D003  mode, mirroring, PPU config, outer bank
 *   $5000/$5400/$5C00 (read) board solder-pad jumper
 *
 * Mapper 90 straps ROM nametables and extended mirroring off, so $D000 bit 5 and
 * $D001 bit 3 have no effect here, and neither has its own implementation yet:
 * this file covers the strap variant (and its banking/IRQ), which is what the
 * ROMs that declare 90 use. Mapper 209/211 are the same chip without that strap
 * and are deliberately left unregistered until ROM nametables are supported,
 * rather than claim `_known()` for a layout that would render wrong.
 *
 * Mask $F803/$F807 means a write with address bit 11 set is ignored, except for
 * the IRQ registers at $C000, which accept it either way.
 */

function register(Mappers) {
  function JYCompany(nes) {
    this.nes = nes;
  }

  JYCompany.prototype = new Mappers[0]();

  // Source 1 of the IRQ counter is the raw PPU A12 line, so the mapper wants to
  // see it. The MMC3-style low-dot filter is not applied: the JY counter counts
  // every rise, and nesdev documents that source as "eight per scanline". The
  // emulator's A12 hook reports the MMC3-filtered rate of one rise per rendered
  // line, so each notification stands in for eight raw rises.
  JYCompany.prototype.irqClockRate = "a12";
  JYCompany.prototype.watchesA12 = true;
  JYCompany.prototype.A12_RISES_PER_SCANLINE = 8;

  JYCompany.prototype.reset = function () {
    Mappers[0].prototype.reset.apply(this);

    // $8000-$8003. In 8 KB mode these name the four 8 KB windows; in 16 KB mode
    // $8001 holds $8000-$BFFF and $8003 holds $C000-$FFFF; in 32 KB mode $8003
    // holds the single 32 KB window.
    this.prgReg = [0, 0, 0, 0];

    // $9000-$9007 (low byte) and $A000-$A007 (high byte). Together they are the
    // CHR bank number in the granularity the current CHR mode works in.
    this.chrRegLsb = [0, 0, 0, 0, 0, 0, 0, 0];
    this.chrRegMsb = [0, 0, 0, 0, 0, 0, 0, 0];

    // $B000-$B007. Only meaningful when ROM nametables are enabled, which the
    // mapper-90 strap prevents. Kept so a save round-trips the writes.
    this.nametableReg = [0, 0, 0, 0, 0, 0, 0, 0];

    // $D000 mode (banking layouts), $D001 mirroring, $D002 PPU config,
    // $D003 outer bank.
    this.mode = 0;
    this.mirroringReg = 0;
    this.ppuConfig = 0;
    this.outerBank = 0;

    // IRQ counter.
    this.irqEnabled = false;
    this.irqPending = false;
    this.irqSource = 0;
    this.irqDirection = 0;
    this.irqPrescalerMask = 0xff;
    this.irqPrescaler = 0;
    this.irqCounter = 0;
    this.irqXor = 0;
    this.irqUnknownMode = 0;

    this.lastA12 = 0;
  };

  // ---- CPU memory map ----

  JYCompany.prototype.write = function (address, value) {
    if (address < 0x8000) {
      Mappers[0].prototype.write.apply(this, arguments);
      return;
    }

    var high = address & 0xf000;

    // $C000-$CFFF: the IRQ registers decode with address bit 11 ignored.
    if (high === 0xc000) {
      this.writeIrq(address & 0x07, value);
      return;
    }

    // Every other register ignores a write with A11 set.
    if ((address & 0x0800) !== 0) {
      return;
    }

    switch (high) {
      case 0x8000:
        this.prgReg[address & 0x03] = value;
        this.updatePrgBanks();
        return;

      case 0x9000:
        this.chrRegLsb[address & 0x07] = value;
        this.updateChrBanks();
        return;

      case 0xa000:
        this.chrRegMsb[address & 0x07] = value;
        this.updateChrBanks();
        return;

      case 0xb000:
        this.nametableReg[address & 0x07] = value;
        return;

      case 0xd000:
        this.writeConfig(address & 0x03, value);
        return;
    }
  };

  // The solder-pad register reports the board's jumper wiring. It is fully
  // decoded at three addresses, which is why they are compared and not masked.
  // Without a board database the "no jumper" wiring is reported, which is what
  // the single-game carts use.
  JYCompany.prototype.load = function (address) {
    if (address === 0x5000 || address === 0x5400 || address === 0x5c00) {
      return 0;
    }
    return Mappers[0].prototype.load.apply(this, arguments);
  };

  // ---- registers ----

  JYCompany.prototype.writeConfig = function (reg, value) {
    switch (reg) {
      case 0:
        this.mode = value;
        this.updatePrgBanks();
        this.updateChrBanks();
        return;

      case 1:
        this.mirroringReg = value;
        this.updateMirroring();
        return;

      case 2:
        this.ppuConfig = value;
        return;

      case 3:
        this.outerBank = value;
        this.updatePrgBanks();
        this.updateChrBanks();
        return;
    }
  };

  JYCompany.prototype.writeIrq = function (reg, value) {
    switch (reg) {
      case 0: // $C000: disable (0) / enable (1) IRQ generation
        if ((value & 0x01) !== 0) {
          this.irqEnabled = true;
        } else {
          this.disableIrq();
        }
        return;

      case 1: // $C001: source, prescaler mask, counting direction
        this.irqSource = value & 0x03;
        this.irqPrescalerMask = (value & 0x04) !== 0 ? 0x07 : 0xff;
        this.irqDirection = (value >> 6) & 0x03;
        return;

      case 2: // $C002: disable and acknowledge
        this.disableIrq();
        return;

      case 3: // $C003: enable
        this.irqEnabled = true;
        return;

      case 4: // $C004: prescaler value (stored XORed with $C006)
        this.irqPrescaler = value ^ this.irqXor;
        return;

      case 5: // $C005: counter value (stored XORed with $C006)
        this.irqCounter = value ^ this.irqXor;
        return;

      case 6: // $C006: XOR value applied to $C004/$C005
        this.irqXor = value;
        return;

      case 7: // $C007: unknown mode configuration
        this.irqUnknownMode = value;
        return;
    }
  };

  // ---- banking ----

  JYCompany.prototype.updatePrgBanks = function () {
    var rom = this.nes.rom;
    var banks8k = rom.romCount * 2;
    var mode = this.mode & 0x03;
    var switchLast = (this.mode & 0x04) !== 0;

    // $D003 bits 1-2 pick a 512 KB outer bank, i.e. 64 x 8 KB.
    var outer = ((this.outerBank >> 1) & 0x03) * 64;

    // The register holds the bank number in the current mode's granularity, so
    // modes 0 and 1 scale up to the 8 KB windows everything is loaded in.
    var scale = mode === 0 ? 4 : mode === 1 ? 2 : 1;

    function at(reg) {
      var bank = mode === 3 ? reverse7(reg & 0x7f) : reg * scale;
      return (outer + bank) % banks8k;
    }

    switch (mode) {
      case 0: // one 32 KB window at $8000
        this.load32kRomBank(
          Math.floor((switchLast ? at(this.prgReg[3]) : banks8k - 1) / 4),
          0x8000,
        );
        break;

      case 1: // 16 KB at $8000, 16 KB at $C000
        this.loadRomBank(Math.floor(at(this.prgReg[1]) / 2), 0x8000);
        this.loadRomBank(
          switchLast ? Math.floor(at(this.prgReg[3]) / 2) : rom.romCount - 1,
          0xc000,
        );
        break;

      default: // modes 2 and 3: four 8 KB windows
        this.load8kRomBank(at(this.prgReg[0]), 0x8000);
        this.load8kRomBank(at(this.prgReg[1]), 0xa000);
        this.load8kRomBank(at(this.prgReg[2]), 0xc000);
        this.load8kRomBank(
          switchLast ? at(this.prgReg[3]) : banks8k - 1,
          0xe000,
        );
        break;
    }
  };

  JYCompany.prototype.updateChrBanks = function () {
    var rom = this.nes.rom;
    if (rom.vromCount === 0) {
      return;
    }
    var mode = (this.mode >> 3) & 0x03;

    // $D003 outer CHR bank, converted to 1 KB units. Bits 3-4 add 512 KB
    // (512 x 1 KB) banks; when the L bit (bit 5) is clear, bit 0 adds a 256 KB
    // bank and the bank register is confined to 256 KB. ROMs of 256 KB keep all
    // of this at zero.
    var outer = ((this.outerBank >> 3) & 0x03) * 512;
    if ((this.outerBank & 0x20) === 0) {
      outer += (this.outerBank & 0x01) * 256;
    }

    var self = this;
    var scale = mode === 0 ? 8 : mode === 1 ? 4 : mode === 2 ? 2 : 1;

    function bank1k(index) {
      var value = (self.chrRegMsb[index] << 8) | self.chrRegLsb[index];
      return outer + value * scale;
    }

    var i;
    switch (mode) {
      case 0: // one 8 KB page for both pattern tables
        for (i = 0; i < 8; i++) {
          this.load1kVromBank(bank1k(0) + i, i * 0x400);
        }
        break;

      case 1: // two 4 KB pages
        for (i = 0; i < 4; i++) {
          this.load1kVromBank(bank1k(0) + i, i * 0x400);
          this.load1kVromBank(bank1k(4) + i, 0x1000 + i * 0x400);
        }
        break;

      case 2: // four 2 KB pages
        for (i = 0; i < 2; i++) {
          this.load1kVromBank(bank1k(0) + i, i * 0x400);
          this.load1kVromBank(bank1k(2) + i, 0x0800 + i * 0x400);
          this.load1kVromBank(bank1k(4) + i, 0x1000 + i * 0x400);
          this.load1kVromBank(bank1k(6) + i, 0x1800 + i * 0x400);
        }
        break;

      default: // eight 1 KB pages
        for (i = 0; i < 8; i++) {
          this.load1kVromBank(bank1k(i), i * 0x400);
        }
        break;
    }
  };

  JYCompany.prototype.updateMirroring = function () {
    // Mapper 90's strap keeps extended mirroring and ROM nametables off, so only
    // the low two bits of $D001 matter. Value 0 wires CIRAM A10 to PPU A10 and 1
    // wires it to PPU A11, which is the same naming the MMC3 register uses; the
    // HORIZONTAL/VERTICAL constants are the other way round (they follow the
    // iNES header), so the two non-single-screen cases swap.
    switch (this.mirroringReg & 0x03) {
      case 0:
        this.setMirroring(this.nes.rom.VERTICAL_MIRRORING);
        break;
      case 1:
        this.setMirroring(this.nes.rom.HORIZONTAL_MIRRORING);
        break;
      case 2:
        this.setMirroring(this.nes.rom.SINGLESCREEN_MIRRORING);
        break;
      case 3:
        this.setMirroring(this.nes.rom.SINGLESCREEN_MIRRORING2);
        break;
    }
  };

  // ---- IRQ counter ----

  JYCompany.prototype.notifyA12 = function (level) {
    this.lastA12 = level === 1;

    // Only source 1 leans on A12; sources 0 (CPU M2), 2 (PPU reads) and 3 (CPU
    // writes) are not wired up, as no JY ROM in the test set selects them.
    if (level !== 1 || this.irqSource !== 1) {
      return;
    }

    for (var i = 0; i < this.A12_RISES_PER_SCANLINE; i++) {
      this.clockIrqCounter();
    }
  };

  // Disabling acknowledges a pending IRQ, inhibits counting and clears the
  // prescaler, per the register description.
  JYCompany.prototype.disableIrq = function () {
    this.irqEnabled = false;
    this.irqPending = false;
    this.irqPrescaler = 0;
  };

  // One clock from the selected source. Counting is inhibited while the IRQ is
  // disabled, so a game that parks the counter between splits does not have it
  // drift. The prescaler runs first: when the part selected by the mask wraps,
  // the counter moves, and when the counter wraps it raises the IRQ.
  JYCompany.prototype.clockIrqCounter = function () {
    if (!this.irqEnabled) {
      return;
    }
    if (this.irqDirection === 0 || this.irqDirection === 3) {
      return;
    }

    if (this.irqDirection === 1) {
      this.irqPrescaler = (this.irqPrescaler + 1) & 0xff;
      if ((this.irqPrescaler & this.irqPrescalerMask) === 0) {
        this.irqCounter = (this.irqCounter + 1) & 0xff;
        if (this.irqCounter === 0) {
          this.raiseIrq();
        }
      }
      return;
    }

    this.irqPrescaler = (this.irqPrescaler - 1) & 0xff;
    if ((this.irqPrescaler & this.irqPrescalerMask) === this.irqPrescalerMask) {
      this.irqCounter = (this.irqCounter - 1) & 0xff;
      if (this.irqCounter === 0xff) {
        this.raiseIrq();
      }
    }
  };

  // Counting only runs while enabled (see clockIrqCounter), so reaching here
  // always means the IRQ should reach the CPU.
  JYCompany.prototype.raiseIrq = function () {
    this.irqPending = true;
    this.nes.cpu.requestIrq(this.nes.cpu.IRQ_NORMAL);
  };

  // ---- power on ----

  JYCompany.prototype.loadROM = function () {
    if (!this.nes.rom.valid) {
      throw new Error("J.Y. Company: Invalid ROM! Unable to load.");
    }

    this.reset();
    this.loadCHRROM();
    this.loadBatteryRam();
    this.loadTrainer();

    // Power-on: mode 0 with the last-bank strap, so $8000-$FFFF comes up as the
    // last 32 KB. The reset vector is there, so booting works before the game
    // writes $D000.
    this.updatePrgBanks();
    this.updateChrBanks();
    this.updateMirroring();

    this.resetIrqHandler();
  };

  JYCompany.prototype.toJSON = function () {
    var s = Mappers[0].prototype.toJSON.apply(this);
    s.prgReg = this.prgReg.slice();
    s.chrRegLsb = this.chrRegLsb.slice();
    s.chrRegMsb = this.chrRegMsb.slice();
    s.nametableReg = this.nametableReg.slice();
    s.mode = this.mode;
    s.mirroringReg = this.mirroringReg;
    s.ppuConfig = this.ppuConfig;
    s.outerBank = this.outerBank;
    s.irqEnabled = this.irqEnabled;
    s.irqPending = this.irqPending;
    s.irqSource = this.irqSource;
    s.irqDirection = this.irqDirection;
    s.irqPrescalerMask = this.irqPrescalerMask;
    s.irqPrescaler = this.irqPrescaler;
    s.irqCounter = this.irqCounter;
    s.irqXor = this.irqXor;
    s.irqUnknownMode = this.irqUnknownMode;
    s.lastA12 = this.lastA12;
    return s;
  };

  JYCompany.prototype.fromJSON = function (s) {
    Mappers[0].prototype.fromJSON.apply(this, arguments);
    this.prgReg = s.prgReg.slice();
    this.chrRegLsb = s.chrRegLsb.slice();
    this.chrRegMsb = s.chrRegMsb.slice();
    this.nametableReg = s.nametableReg.slice();
    this.mode = s.mode;
    this.mirroringReg = s.mirroringReg;
    this.ppuConfig = s.ppuConfig;
    this.outerBank = s.outerBank;
    this.irqEnabled = s.irqEnabled;
    this.irqPending = s.irqPending;
    this.irqSource = s.irqSource;
    this.irqDirection = s.irqDirection;
    this.irqPrescalerMask = s.irqPrescalerMask;
    this.irqPrescaler = s.irqPrescaler;
    this.irqCounter = s.irqCounter;
    this.irqXor = s.irqXor;
    this.irqUnknownMode = s.irqUnknownMode;
    this.lastA12 = s.lastA12;
    this.updatePrgBanks();
    this.updateChrBanks();
    this.updateMirroring();
  };

  // Reverses bits 0-6 of a 7-bit bank number. Only PRG mode 3 uses it, on the
  // one multicart the ASIC was documented from.
  function reverse7(value) {
    var out = 0;
    for (var i = 0; i < 7; i++) {
      out = (out << 1) | ((value >> i) & 1);
    }
    return out;
  }

  Mappers[90] = JYCompany;
}

module.exports = { register: register };
